import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';

const capabilities = [
  'AI readiness diagnostics',
  'Enterprise AI transformation strategy',
  'Infrastructure pathway advisory (with trusted compute partners)',
  'Private AI ecosystem architecture',
  'Long-horizon AI deployment planning',
  'Sovereign-grade AI advisory',
  'AI-native organisational redesign',
];

const helpItems = [
  'Diagnose intelligence bottlenecks',
  'Assess AI readiness across operations',
  'Architect transformation pathways',
  'Align infrastructure with business strategy',
  'Design and commission scalable intelligence environments',
  'Modernise enterprise decision systems',
  'Future-proof operational intelligence',
];

const AIInfrastructureSection = () => {
  return (
    <section id="ai-infrastructure" className="relative py-24 lg:py-32" data-testid="ai-infrastructure-section">
      <div className="section-divider mb-20" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'rgba(201,162,39,0.6)' }}>
            Enterprise Evolution
          </p>
          <h2
            className="text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.1em] text-white mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
            data-testid="ai-infrastructure-title"
          >
            AI Infrastructure &
            <br className="hidden md:block" />
            Strategic Intelligence&#8482;
          </h2>
        </motion.div>

        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>
            End-to-end AI infrastructure pathways for organisations preparing for the next era of machine intelligence.
          </p>
          <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Celestial Minds&#8482; works with trusted infrastructure and compute partners across North America and Europe to help enterprises, institutions, and emerging ventures deploy scalable AI environments aligned with their operational, strategic, and regulatory realities.
          </p>
        </motion.div>

        {/* Two-column: What we help with + Capabilities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
          {/* Left: What we help organisations do */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-glow p-8"
            style={{
              background: 'rgba(10,22,40,0.4)',
              border: '1px solid rgba(201,162,39,0.12)',
              borderRadius: '4px',
            }}
          >
            <p className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: 'rgba(201,162,39,0.6)' }}>
              Our role is not limited to implementation
            </p>
            <h3
              className="text-base md:text-lg tracking-wider text-white mb-6"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              We Help Organisations
            </h3>
            <ul className="space-y-3">
              {helpItems.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#c9a227' }} />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right: Capabilities */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="card-glow p-8"
            style={{
              background: 'rgba(10,22,40,0.4)',
              border: '1px solid rgba(201,162,39,0.12)',
              borderRadius: '4px',
            }}
          >
            <p className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: 'rgba(201,162,39,0.6)' }}>
              Scope of engagement
            </p>
            <h3
              className="text-base md:text-lg tracking-wider text-white mb-6"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              Capabilities
            </h3>
            <ul className="space-y-3">
              {capabilities.map((cap) => (
                <li key={cap} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#c9a227' }} />
                  {cap}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Closing statement */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center mb-16"
        >
          <p
            className="text-base md:text-lg italic leading-relaxed"
            style={{ color: '#c9a227', fontFamily: "'Cormorant Garamond', serif" }}
          >
            &ldquo;Most organisations do not yet require more AI tools.
            <br />
            They require a fundamentally different intelligence architecture.&rdquo;
          </p>
        </motion.div>

        {/* AI Readiness Assessment CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
          data-testid="ai-readiness-cta"
        >
          <h3
            className="text-lg md:text-xl font-light tracking-[0.1em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            AI Readiness Assessment&#8482;
          </h3>
          <p className="text-sm leading-relaxed max-w-2xl mx-auto mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Evaluate operational AI maturity, intelligence bottlenecks, infrastructure preparedness,
            organisational adaptability, and long-horizon transformation readiness.
          </p>
          <p className="text-xs mb-8" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Most organisations do not know where AI creates leverage, how to modernise intelligently, or what infrastructure they actually require. We become the intelligence layer before deployment.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent('prefill-contact', {
                detail: { subject: 'AI Readiness Assessment Request', requestType: 'ai_readiness' },
              }));
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary inline-flex items-center gap-2 text-xs uppercase tracking-widest mb-4"
            data-testid="ai-readiness-cta-link"
          >
            Request an AI Readiness Assessment
          </a>
          <p className="text-xs mt-4" style={{ color: 'rgba(255,255,255,0.35)' }}>
            <a
              href="mailto:invitation@celestialmindsfze.com"
              className="inline-flex items-center gap-1.5 hover:text-white/60 transition-colors"
            >
              <Mail className="w-3 h-3" />
              invitation@celestialmindsfze.com
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AIInfrastructureSection;
